// src/pages/expenses/ProfitReport.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../utils/config";
import AsyncSelect from "react-select/async";
import Select from "react-select";

export default function ProfitReport() {
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // ----- helpers -----
  const fmtINR = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  const chipTone = (v) =>
    v > 0
      ? "bg-emerald-50 text-emerald-700"
      : v < 0
      ? "bg-rose-50 text-rose-700"
      : "bg-slate-50 text-slate-700";

  const rowTone = (v) =>
    v > 0
      ? "hover:bg-emerald-50/60"
      : v < 0
      ? "hover:bg-rose-50/60"
      : "hover:bg-slate-50";

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 40,
      borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": { borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1" },
      borderRadius: 10,
      fontSize: 14,
    }),
    menu: (base) => ({ ...base, zIndex: 30 }),
  };

  // ----- default Financial Year -----
  function getCurrentFYDates() {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    let from, to;
    if (month >= 4) {
      from = new Date(year, 3, 1);
      to = new Date(year + 1, 2, 31);
    } else {
      from = new Date(year - 1, 3, 1);
      to = new Date(year, 2, 31);
    }
    const f = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    return { start: f(from), end: f(to) };
  }

  useEffect(() => {
    const { start, end } = getCurrentFYDates();
    setStartDate(start);
    setEndDate(end);
  }, []);

  // ----- remote search -----
  const loadCustomerOptions = async (inputValue) => {
    if ((inputValue || "").length < 3) return [];
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/customers`, {
        params: { q: inputValue },
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.map((c) => ({ value: c.id, label: c.name }));
    } catch (err) {
      console.error("Failed to search customers:", err);
      return [];
    }
  };

  const fetchInvoices = async (customerId) => {
    const token = localStorage.getItem("token");
    try {
      const url = customerId
        ? `${API_BASE_URL}/api/invoices?customer_id=${customerId}`
        : `${API_BASE_URL}/api/invoices`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(
        res.data.map((inv) => ({
          value: inv.id,
          label: `INV-${inv.id} (${new Date(inv.created_at).toLocaleDateString(
            "en-GB"
          )})`,
        }))
      );
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
    }
  };

  useEffect(() => {
    if (selectedCustomer) fetchInvoices(selectedCustomer.value);
    else {
      setInvoices([]);
      setSelectedInvoice(null);
    }
  }, [selectedCustomer]);

  const fetchReport = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/expenses/profit`, {
        params: {
          startDate,
          endDate,
          customerId: selectedCustomer?.value || null,
          invoiceId: selectedInvoice?.value || null,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(res.data);
    } catch (err) {
      console.error(err);
      setReport(null);
    }
  };

  // consume report safely
  const invoiceProfits = Array.isArray(report)
    ? report
    : report
    ? [report]
    : [];

  // totals
  const totals = useMemo(
    () => ({
      sale: invoiceProfits.reduce((a, i) => a + (i.saleValue || 0), 0),
      purchase: invoiceProfits.reduce((a, i) => a + (i.purchaseCost || 0), 0),
      gross: invoiceProfits.reduce((a, i) => a + (i.grossProfit || 0), 0),
      doctor: invoiceProfits.reduce(
        (a, i) => a + (i.doctorCommission || 0),
        0
      ),
      direct: invoiceProfits.reduce((a, i) => a + (i.directExpense || 0), 0),
      net: invoiceProfits.reduce((a, i) => a + (i.netProfit || 0), 0),
    }),
    [invoiceProfits]
  );

  return (
    <div className="p-4 md:p-6">
      <h1 className="mb-4 text-2xl font-semibold text-slate-900">
        Profit Report
      </h1>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <AsyncSelect
          cacheOptions
          loadOptions={loadCustomerOptions}
          defaultOptions={[]}
          value={selectedCustomer}
          onChange={setSelectedCustomer}
          isClearable
          placeholder="Search Customer…"
          styles={selectStyles}
          className="min-w-[240px]"
        />
        <Select
          options={invoices}
          value={selectedInvoice}
          onChange={setSelectedInvoice}
          isClearable
          placeholder="Select Invoice…"
          isDisabled={!selectedCustomer}
          styles={selectStyles}
          className="min-w-[220px]"
        />
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <button
          onClick={fetchReport}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 active:scale-[.98]"
        >
          Generate
        </button>
      </div>

      {/* KPI cards */}
      {invoiceProfits.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="text-xs text-slate-500">Sale</div>
            <div className="text-lg font-semibold tabular-nums">
              {fmtINR(totals.sale)}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="text-xs text-slate-500">Purchase</div>
            <div className="text-lg font-semibold tabular-nums">
              {fmtINR(totals.purchase)}
            </div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
            <div className="text-xs text-emerald-700">Gross Profit</div>
            <div className="text-lg font-semibold tabular-nums text-emerald-800">
              {fmtINR(totals.gross)}
            </div>
          </div>
          <div
            className={`rounded-xl p-3 ${
              totals.net > 0
                ? "border-emerald-200 bg-emerald-50/40"
                : totals.net < 0
                ? "border-rose-200 bg-rose-50/40"
                : "border-slate-200 bg-slate-50"
            } border`}
          >
            <div className="text-xs text-slate-700">Net Profit</div>
            <div className="text-lg font-semibold tabular-nums">
              {fmtINR(totals.net)}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {invoiceProfits.length > 0 ? (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-slate-700">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Invoice</th>
                <th className="px-3 py-2 text-right font-medium">Sale Value</th>
                <th className="px-3 py-2 text-right font-medium">
                  Purchase Cost
                </th>
                <th className="px-3 py-2 text-right font-medium">Gross</th>
                <th className="px-3 py-2 text-right font-medium">
                  Doctor Commission
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  Direct Expense
                </th>
                <th className="px-3 py-2 text-right font-medium">Net Profit</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-slate-50/40">
              {invoiceProfits.map((inv) => (
                <tr
                  key={inv.invoice_id}
                  className={`border-t border-slate-100 ${rowTone(
                    Number(inv.netProfit)
                  )}`}
                >
                  <td className="px-3 py-2">
                    <span className="font-medium text-blue-700">
                      INV-{inv.invoice_id}
                    </span>
                  </td>

                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtINR(inv.saleValue)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtINR(inv.purchaseCost)}
                  </td>

                  {/* Gross chip */}
                  <td className="px-3 py-2 text-right">
                    <span
                      className={`inline-flex min-w-[88px] justify-end rounded-full px-2 py-0.5 tabular-nums ${chipTone(
                        Number(inv.grossProfit)
                      )}`}
                    >
                      {fmtINR(inv.grossProfit)}
                    </span>
                  </td>

                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtINR(inv.doctorCommission)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtINR(inv.directExpense)}
                  </td>

                  {/* Net chip */}
                  <td className="px-3 py-2 text-right">
                    <span
                      className={`inline-flex min-w-[88px] justify-end rounded-full px-2 py-0.5 tabular-nums ${chipTone(
                        Number(inv.netProfit)
                      )}`}
                    >
                      {fmtINR(inv.netProfit)}
                    </span>
                  </td>
                </tr>
              ))}

              {/* Totals */}
              <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtINR(totals.sale)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtINR(totals.purchase)}
                </td>
                <td className="px-3 py-2 text-right">
                  <span
                    className={`inline-flex min-w-[88px] justify-end rounded-full px-2 py-0.5 tabular-nums ${chipTone(
                      totals.gross
                    )}`}
                  >
                    {fmtINR(totals.gross)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtINR(totals.doctor)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtINR(totals.direct)}
                </td>
                <td className="px-3 py-2 text-right">
                  <span
                    className={`inline-flex min-w-[88px] justify-end rounded-full px-2 py-0.5 tabular-nums ${chipTone(
                      totals.net
                    )}`}
                  >
                    {fmtINR(totals.net)}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        report && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500 shadow-sm">
            No invoice data found for selected filters.
          </div>
        )
      )}
    </div>
  );
}
